import { Layers } from "lucide-react";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";
import {
  blogPageCount,
  useBlogPosts,
  useBlogTopic,
} from "@/tools/blog/hooks/use-blog-data";
import { useScrollTopOnChange } from "@/tools/blog/hooks/use-scroll-top-on-change";
import { BlogBreadcrumb } from "../components/BlogBreadcrumb";
import { BlogPagination } from "../components/BlogPagination";
import { BlogPostCard } from "../components/BlogPostCard";
import { BlogPostSkeleton } from "../components/BlogPostSkeleton";

/** Topic hub (pillar page): /blog/<tool>/<topic> — posts in this topic. */
export function BlogTopic({
  tool,
  topic,
  page = 1,
}: {
  tool: string;
  topic: string;
  page?: number;
}) {
  const locale = getLocale();
  useScrollTopOnChange(page);
  const { data: topicData } = useBlogTopic(tool, topic, locale);
  const { data, isLoading } = useBlogPosts(locale, tool, topic, page);
  const posts = data?.items ?? [];
  const pageCount = blogPageCount(data?.total ?? 0);

  return (
    <div className="relative">
      <div className="pointer-events-none absolute inset-0 gradient-mesh" />
      <div className="pointer-events-none absolute inset-0 grid-pattern-fine opacity-20" />

      <div className="relative mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="animate-slide-up">
          <BlogBreadcrumb
            items={[
              { label: m.tool_blog_name(), to: "/blog" },
              {
                label: topicData?.toolName ?? tool,
                to: "/blog/$tool",
                params: { tool },
              },
              { label: topicData?.name ?? topic },
            ]}
          />
        </div>

        {/* Topic header */}
        <div className="animate-slide-up delay-100 mt-8 rounded-xl border border-border/30 bg-card/50 p-8 opacity-0 backdrop-blur-sm">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
              <Layers className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
                {topicData?.name ?? topic}
              </h1>
              {topicData?.description && (
                <p className="mt-2 max-w-2xl text-muted-foreground">
                  {topicData.description}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Posts */}
        <div className="mt-10">
          {isLoading ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <BlogPostSkeleton key={i} />
              ))}
            </div>
          ) : posts.length > 0 ? (
            <>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {posts.map((post, i) => (
                  <div
                    key={post.id}
                    className="animate-slide-up opacity-0"
                    style={{ animationDelay: `${200 + i * 100}ms` }}
                  >
                    <BlogPostCard post={post} />
                  </div>
                ))}
              </div>
              <BlogPagination
                page={page}
                pageCount={pageCount}
                to="/blog/$tool/$topic"
                params={{ tool, topic }}
              />
            </>
          ) : (
            <p className="py-16 text-center text-muted-foreground">
              {m.blog_no_posts()}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
